
// * ========================================================================
// * Summary chart
// * ========================================================================

import CovidChart from './covid-chart.js';
import moment from 'moment';
import { roundTo } from '../helpers';
import storage from './../storage';

const summary = (function() {
    let thisChart;
    const average = (arr) => arr.reduce((a, b) => a + b) / arr.length;
    const firstDates = ['',moment('2020-03-01'),moment('2020-07-01'),moment('2021-07-01'),moment('2022-07-01')];
    let firstDate = firstDates[0];

    const multiselectSettings = {
        requireCount: false,
        selectAll: false,
        valueType: 'string',
        idField: 'id',
        colorField: 'borderColor',
        inputName: 'summary-multiselect',
        selectElemId: 'summary-multiselect',
        colorElemId: 'summary-multiselect-colors',
        countElemId: 'summary-multiselect-count'
    };

    /**
     *
     * Create new chart
     *
     * @param {Object} data - covid data
     * @param {Object} options - chart options
     * @param {Array} selected - selected datasets
     *
     * @returns {Object} - chart
     */
    function createChartData(data, options, selected) {

        firstDate = options.wave && typeof firstDates[options.wave] !== 'undefined' ? firstDates[options.wave] : '';
        if (!firstDate) {
            firstDate = options.firstDate; // User selected first date
        }

        const datasets = [{
            label: 'Varmistetut tartunnat / '+options.rateDays+' vrk ka.',
            id: 'infections',
            borderColor: '#ff7f00',
            borderWidth: 3,
            fill: false,
            selected: selected.indexOf('infections') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            yAxisID: 'y',
            order: 7,
        },
        /*{
            label: 'Testit / '+options.rateDays+' vrk ka.',
            id: 'tests',
            borderColor: '#396603',
            borderWidth: 3,
            fill: false,
            selected: selected.indexOf('tests') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            yAxisID: 'y2',
            order: 8
        },
        {
            label: 'Positiiviset % testatuista / '+options.rateDays+' vrk ka.',
            id: 'casesPerTests',
            borderColor: '#f9c004',
            borderWidth: 3,
            fill: false,
            selected: selected.indexOf('casesPerTests') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            yAxisID: 'y3',
            order: 9
        },*/
        {
            label: 'Sairaalahoidossa 7 vrk mediaani',
            id: 'hospital',
            borderColor: '#0293d6',
            borderWidth: 3,
            fill: false,
            selected: selected.indexOf('hospital') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            tension: 0.4,
            type: 'LineWithLine',
            yAxisID: 'y5',
            order: 10,
        },
        {
            label: 'Kuolleet / 21 vrk ka.',
            id: 'dead',
            borderColor: '#666666',
            borderWidth: 3,
            fill: false,
            selected: selected.indexOf('dead') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            tension: 0.4,
            type: 'LineWithLine',
            yAxisID: 'y5',
            order: 11
        },
        {
            label: 'Koronavirusta jätevedessä ka.',
            id: 'sewage',
            borderColor: '#e80a9a',
            borderWidth: 3,
            hideChange: true,
            tooltipHide: true,
            fill: false,
            selected: selected.indexOf('sewage') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'line',
            yAxisID: 'y6',
            tension: 0.3,
            order: 99
        },
        {
            label: 'Koronavirusta jätevedessä ka. (vanha)',
            id: 'sewage-2',
            multiselectShowFor: 'sewage',
            multiselectHidden: true,
            borderColor: '#e80a9a',
            borderWidth: 3,
            hideChange: true,
            tooltipHide: true,
            fill: false,
            selected: selected.indexOf('sewage') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'line',
            yAxisID: 'y6',
            tension: 0.3,
            order: 99
        },
        {
            label: 'Koronavirusta jätevedessä ka. (vanhin)',
            id: 'sewage-3',
            multiselectShowFor: 'sewage',
            multiselectHidden: true,
            borderColor: '#e80a9a',
            borderWidth: 3,
            hideChange: true,
            tooltipHide: true,
            fill: false,
            selected: selected.indexOf('sewage') !== -1,
            data: [],
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'line',
            yAxisID: 'y6',
            tension: 0.3,
            order: 99
        },
        {
            label: 'Koronavirusta jätevedessä ka.',
            id: 'sewage-guide',
            multiselectHidden: true,
            multiselectShowFor: 'sewage',
            tooltipHideForZero: true,
            hideChange: true,
            labelColor: '#e80a9a',
            backgroundColor: 'rgba(0, 0, 0, 0)',
            borderColor: 'rgba(0, 0, 0, 0.0)',
            borderWidth: 3,
            pointRadius: 0,
            pointHoverRadius: 5,
            fill: false,
            selected: selected.indexOf('sewage') !== -1,
            data: [],
            type: 'LineWithLine',
            yAxisID: 'y6',
            order: 4
        },
        {
            label: 'Koronavirusta jätevedessä ka. (vanha)',
            id: 'sewage-guide-old',
            multiselectHidden: true,
            multiselectShowFor: 'sewage',
            tooltipHideForZero: true,
            hideChange: true,
            labelColor: '#e80a9a',
            backgroundColor: 'rgba(0, 0, 0, 0)',
            borderColor: 'rgba(0, 0, 0, 0.0)',
            fill: false,
            selected: selected.indexOf('sewage') !== -1,
            data: [],
            borderWidth: 3,
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            yAxisID: 'y6',
            order: 5
        },
        {
            label: 'Koronavirusta jätevedessä ka. (vanhin)',
            id: 'sewage-guide-oldest',
            multiselectHidden: true,
            multiselectShowFor: 'sewage',
            tooltipHideForZero: true,
            hideChange: true,
            labelColor: '#e80a9a',
            backgroundColor: 'rgba(0, 0, 0, 0)',
            borderColor: 'rgba(0, 0, 0, 0.0)',
            fill: false,
            selected: selected.indexOf('sewage') !== -1,
            data: [],
            borderWidth: 3,
            pointRadius: 0,
            pointHoverRadius: 5,
            type: 'LineWithLine',
            yAxisID: 'y6',
            order: 6
        }];

        const newProcessingDate = moment('2023-01-01');

        const vaccineColors = ['#bbc892','#94e60d','#78b907','#5f9304','#4b7306'];
        const vaccineKeys = [];
        const vaccineDoses = data.vaccines.doses.map((item) => {
            const vaccineKey = 'vaccine'+item.dose;
            vaccineKeys.push(vaccineKey);

            datasets.push({
                label: 'Rokotus % '+item.dose+'.annos',
                id: vaccineKey,
                borderColor: vaccineColors[item.dose - 1],
                borderWidth: 3,
                fill: false,
                selected: selected.indexOf(vaccineKey) !== -1,
                data: [],
                pointRadius: 0,
                pointHoverRadius: 5,
                type: 'LineWithLine',
                yAxisID: 'y4'
            });

            return {
                dose: item.dose,
                key: vaccineKey,
                dataset: datasets.length - 1
            };
        });

        const dayIdxMap = {};
        const summaryData = [];

        // Infections (7/14 day average)
        let dayIdx = 0;
        const infDelay = data.confirmed.days.length - 3;
        for (let idx = 0; idx < data.confirmed.days.length; idx++) {
            const item = data.confirmed.days[idx];
            const date = moment(item.date);

            if (idx > infDelay) {
                // no point of showing these because of delay
                continue;
            }

            if (date >= firstDate) {
                // Get past 7/14 days values
                let firstDay = idx - options.rateDays;
                firstDay = firstDay < 0 ? 0 : firstDay;

                const pastWeekValues = [];
                for (let x = firstDay; x < idx; x++) {
                    let dayValue = data.confirmed.days[x];
                    pastWeekValues.push(dayValue.dayCount);
                }
                const averagePrecise = pastWeekValues.length ? average(pastWeekValues) : 0;
                const weekAverage = Math.round(averagePrecise);

                summaryData.push({
                    dateString: item.date,
                    date: date,
                    infections: weekAverage,
                    infectionsPrecise: averagePrecise
                });

                dayIdxMap[item.date] = dayIdx;
                dayIdx++;
            }
        }

        // Add dates until today
        const today = moment();
        const todayStr = today.format().split('T')[0];
        if (typeof dayIdxMap[todayStr] === 'undefined') {
            let lastDay = moment(summaryData[summaryData.length-1].dateString).add(1, 'days');
            while (lastDay.isBefore(today, 'day') || lastDay.isSame(today, 'day')) {
                let dayStr = lastDay.format().split('T')[0];

                summaryData.push({
                    dateString: dayStr,
                    date: moment(dayStr)
                });
                dayIdxMap[dayStr] = dayIdx;
                dayIdx++;
                lastDay.add(1, 'days');
            }
        }

        // Tests (7/14 day average)
        /*
        const testDelay = moment().add(-5,'days');
        for (let idx = 0; idx < data.tests.days.length; idx++) {
            const item = data.tests.days[idx];
            const date = moment(item.date);

            if (date > testDelay) {
                // no point of showing these because of delay
                continue;
            }

            // Push only after first visible date
            if (date >= firstDate) {
                // Get past 7 days values
                let firstDay = idx - options.rateDays;
                firstDay = firstDay < 0 ? 0 : firstDay;

                const pastWeekValues = [];
                for (let x = firstDay; x < idx; x++) {
                    let dayValue = data.tests.days[x];
                    pastWeekValues.push(dayValue.count);
                }

                const averagePrecise = pastWeekValues.length ? average(pastWeekValues) : 0;
                const weekAverage = Math.round(averagePrecise);

                let dIdx = typeof dayIdxMap[item.date] !== 'undefined' ? dayIdxMap[item.date] : false;
                if (typeof dIdx === 'number') {
                    summaryData[dIdx].testsPrecise = averagePrecise;
                    summaryData[dIdx].tests = weekAverage;
                }
            }
        }*/

        // Hospitalizations
        for (let idx = 0; idx < data.hospital.weeks.length; idx++) {
            let day = data.hospital.weeks[idx];
            let dIdx = typeof dayIdxMap[day.date] !== 'undefined' ? dayIdxMap[day.date] : false;

            if (typeof dIdx === 'number') {
                summaryData[dIdx].hospital = day.hospitalized;
            }
        }

        // Deaths
        const deadDelay = moment().add(-21,'days');
        for (let idx = 0; idx < data.dead.days.length; idx++) {
            let deadDay = data.dead.days[idx];
            const dateObj = moment(deadDay.date);
            if (dateObj > deadDelay) {
                // no point of showing these because of delay
                continue;
            }
            let deaths = deadDay.count > 0 ? deadDay.count : 0;

            let dIdx = typeof dayIdxMap[deadDay.date] !== 'undefined' ? dayIdxMap[deadDay.date] : false;
            if (typeof dIdx === 'number') {
                summaryData[dIdx].deaths = deaths;
            }
        }

        // Vaccines all ages per week
        const weekDataMap = {};
        let lastWeekDate = null;
        let lastValueDIdx = null;
        for (let dose of data.vaccines.doses) {
            for (let week of dose.weeks) {
                let weekData = typeof weekDataMap[week.week] !== 'undefined' ? weekDataMap[week.week] : null;
                let weekDate = weekData ? weekData.date : null;
                let dIdx = weekData ? weekData.dIdx : null;

                if (!weekData) {
                    const weekName = week.week;
                    const weekArr = weekName.split('_');
                    const weekNumber = parseInt(weekArr[1]);
                    weekDate = moment().year(weekArr[0]).day(1).week(weekNumber);
                    weekDate.set({
                        hour: 0,
                        minute: 0,
                        second: 0
                    });

                    const weekDateStr = weekDate.format('YYYY-MM-DD');
                    dIdx = typeof dayIdxMap[weekDateStr] !== 'undefined' ? dayIdxMap[weekDateStr] : false;

                    // Check if the end of the week is still visible
                    if (typeof dIdx !== 'number') {
                        const weekDateEnd = weekDate.clone().weekday(7);
                        const weekDateEndStr = weekDateEnd.format('YYYY-MM-DD');

                        if (typeof dayIdxMap[weekDateEndStr] !== 'undefined') {
                            // Day index is the first day of data
                            dIdx = 0;
                        }
                    }

                    weekDataMap[week.week] = {
                        date: weekDate,
                        dIdx
                    };
                }

                if (typeof dIdx === 'number') {
                    const allAges = week.ages.filter((item) => item.age === 'all_ages');
                    if (allAges.length) {
                        const percent = allAges[0].percentage > 0 ? roundTo(allAges[0].percentage, 2) : 0;
                        const summaryKey = 'vaccine'+dose.dose;
                        summaryData[dIdx][summaryKey] = percent;
                    }

                    lastWeekDate = weekDate.day(6).add('1', 'day').format('YYYY-MM-DD');
                    lastValueDIdx = dIdx;
                }
            }
        }

        // Sewage
        if (typeof data.sewage !== 'undefined' && data.sewage.weeks.length) {
            const sewageData = data.sewage.weeks;
            for (let idx = (sewageData.length - 1); idx >= 0; idx--) {
                const item = sewageData[idx];
                const date = moment(item.date);

                // Push only after first visible date
                if (date >= firstDate) {
                    let firstDay = idx + 1;
                    firstDay = firstDay > (sewageData.length - 1) ? idx : firstDay;

                    const pastWeekValues = [];
                    const pastWeekValuesOld = [];
                    const pastWeekValuesOldest = [];
                    for (let x = idx; x <= firstDay; x++) {
                        if (sewageData[x].weightedTotal !== null) {
                            if (moment(sewageData[x].date) >= newProcessingDate) {
                                pastWeekValues.push(sewageData[x].weightedTotal);
                            } else {
                                pastWeekValuesOld.push(sewageData[x].weightedTotal);
                            }
                        }
                        if (sewageData[x].weightedTotalOld !== null) {
                            pastWeekValuesOldest.push(sewageData[x].weightedTotalOldEvened);
                        }
                    }

                    let weeksAverage = pastWeekValues.length ? average(pastWeekValues) : 0;
                    weeksAverage = Math.round(weeksAverage);

                    let weeksAverageOld = pastWeekValuesOld.length ? average(pastWeekValuesOld) : 0;
                    weeksAverageOld = Math.round(weeksAverageOld);

                    let weeksAverageOldest = pastWeekValuesOldest.length ? average(pastWeekValuesOldest) : 0;
                    weeksAverageOldest = Math.round(weeksAverageOldest);

                    let dIdx = typeof dayIdxMap[item.date] !== 'undefined' ? dayIdxMap[item.date] : false;
                    if (typeof dIdx === 'number') {
                        summaryData[dIdx].sewage = pastWeekValues.length ? weeksAverage : NaN;
                        summaryData[dIdx].sewageOld = date < newProcessingDate && pastWeekValuesOld.length ? weeksAverageOld : NaN;
                        summaryData[dIdx].sewageOldest = pastWeekValuesOldest.length ? weeksAverageOldest : NaN;
                    }
                }
            }
        }

        // Set an end for the week value
        let dIdx = typeof dayIdxMap[lastWeekDate] !== 'undefined' ? dayIdxMap[lastWeekDate] : false;
        if (typeof dIdx === 'number' && dIdx > lastValueDIdx) {
            for (let dose of vaccineDoses) {
                summaryData[dIdx][dose.key] = summaryData[lastValueDIdx][dose.key];
            }
        }

        // Fill in missing day data
        let previous = {
            /*tests: 0,
            hospital: 0,
            deaths: 0*/
        };

        let keys = ['hospital','deaths'];
        let highestCases = 0;
        let lastValueIdx = {
            hospital: null,
            deaths: null
        };

        for (let vaccineKey of vaccineKeys) {
            lastValueIdx[vaccineKey] = null;
            keys.push(vaccineKey);
        }

        for (const [i, summaryDay] of summaryData.entries()) {
            for (let key of keys) {
                // Fill only after first found value
                if (typeof summaryDay[key] === 'undefined') {
                    // Only if previous is set and is not last days
                    if (typeof previous[key] !== 'undefined') {
                        summaryDay[key] = previous[key];

                        // Set zero new deaths for today
                        if (key === 'deaths') {
                            summaryDay[key] = 0;
                        }
                    }
                } else {
                    previous[key] = summaryDay[key];
                    lastValueIdx[key] = i;
                }
            }

            // Calculate cases per tests (7/14 day average)
            /*
            let casesPerTests = 0;
            if (summaryDay.infectionsPrecise && summaryDay.testsPrecise) {
                casesPerTests = (summaryDay.infectionsPrecise / summaryDay.testsPrecise) * 100;
                casesPerTests = Math.round(casesPerTests * 10) / 10;
            }

            if (casesPerTests > highestCases) {
                highestCases = casesPerTests;
            }

            if (summaryDay.infectionsPrecise && summaryDay.testsPrecise) {
                summaryDay['casesPerTests'] = casesPerTests;
            }*/
        }

        // Add to datasets
        for (const [i, day] of summaryData.entries()) {
            datasets[0].data.push({
                x: day.date,
                y: day.infections
            });
            /*
            datasets[1].data.push({
                x: day.date,
                y: day.tests
            });
            datasets[2].data.push({
                x: day.date,
                y: day.casesPerTests
            });*/
            if (lastValueIdx.hospital && lastValueIdx.hospital >= i) {
            //if (typeof day.hospital !== 'undefined') {
                datasets[1].data.push({
                    x: day.date,
                    y: day.hospital
                });
            }
            //}
            if (lastValueIdx.deaths && lastValueIdx.deaths >= i) {
            //if (typeof day.hospital !== 'undefined') {
                datasets[2].data.push({
                    x: day.date,
                    y: day.deaths,
                    deaths: day.deaths
                });
            }
            //}

            if (typeof day.sewage !== 'undefined') {
                datasets[3].data.push({
                    x: day.date,
                    y: typeof day.sewage !== 'undefined' ? day.sewage : NaN
                });
            }
            if (typeof day.sewageOld !== 'undefined') {
                datasets[4].data.push({
                    x: day.date,
                    y: typeof day.sewageOld !== 'undefined' ? day.sewageOld : NaN
                });
            }
            if (typeof day.sewageOldest !== 'undefined') {
                datasets[5].data.push({
                    x: day.date,
                    y: typeof day.sewageOldest !== 'undefined' ? day.sewageOldest : NaN
                });
            }
            // Hidden sewage guides
            datasets[6].data.push({
                x: day.date,
                y: day.sewage || 0
            });
            datasets[7].data.push({
                x: day.date,
                y: day.sewageOld || 0
            });
            datasets[8].data.push({
                x: day.date,
                y: day.sewageOldest || 0
            });


            for (let dose of vaccineDoses) {
                if (lastValueIdx[dose.key] && lastValueIdx[dose.key] >= i) {
                    datasets[dose.dataset].data.push({
                        x: day.date,
                        y: day[dose.key]
                    });
                }
            }
        }

        // Count average deaths
        for (let i = 0; i < datasets[2].data.length; i++) {
            // Get past 3 week values
            let deathFirstDay = i - 21;
            deathFirstDay = deathFirstDay < 0 ? 0 : deathFirstDay;

            let deathsInTimePeriod = 0;
            if (i > 0) {
                for (let x2 = deathFirstDay; x2 < i; x2++) {
                    deathsInTimePeriod += (0 + datasets[2].data[x2].deaths);
                }
            }

            datasets[2].data[i].y = deathsInTimePeriod;
        }

        // Create line segments
        const lag = 4 * 7; // weeks

        const hospitalDataLagIdx = datasets[1].data.length - lag;
        const dashedHospital = function (ctx, value) {
            if (ctx.p0DataIndex > hospitalDataLagIdx) {
                return value;
            }
            return undefined;
        }

        datasets[1].segment = {
            borderColor: ctx => dashedHospital(ctx, 'rgb(2 147 214 / 50%)'),
            borderDash: ctx => dashedHospital(ctx, [6, 6]),
        };

        // Deaths
        const deadDataLagIdx = datasets[2].data.length - lag;
        const dashedDead = function (ctx, value) {
            if (ctx.p0DataIndex > deadDataLagIdx) {
                return value;
            }
            return undefined;
        }

        datasets[2].segment = {
            borderColor: ctx => dashedDead(ctx, 'rgb(102 102 102 / 50%)'),
            borderDash: ctx => dashedDead(ctx, [6, 6]),
        };

        // Tapaukset
        const countLag = 4 * 7;
        const dataLagIdx = datasets[0].data.length - countLag;
        const dashed = function (ctx, value) {
            if (ctx.p0DataIndex > dataLagIdx) {
                return value;
            }
            return undefined;
        }

        datasets[0].segment = {
            borderColor: ctx => dashed(ctx, 'rgb(102 102 102 / 50%)'),
            borderDash: ctx => dashed(ctx, [6, 6]),
        };

        // Testit
        /*
        const testDataLagIdx = datasets[1].data.length - countLag;
        const dashedTests = function (ctx, value) {
            if (ctx.p0DataIndex > testDataLagIdx) {
                return value;
            }
            return undefined;
        }

        datasets[1].segment = {
            borderColor: ctx => dashedTests(ctx, 'rgb(102 102 102 / 50%)'),
            borderDash: ctx => dashedTests(ctx, [6, 6]),
        };

        // Positiiviset
        const posDataLagIdx = datasets[2].data.length - countLag;
        const dashedPos = function (ctx, value) {
            if (ctx.p0DataIndex > posDataLagIdx) {
                return value;
            }
            return undefined;
        }

        datasets[2].segment = {
            borderColor: ctx => dashedPos(ctx, 'rgb(102 102 102 / 50%)'),
            borderDash: ctx => dashedPos(ctx, [6, 6]),
        };*/

        return datasets;
    }

    function getSelected() {
        // Initially selected
        //let selected = storage.getData('summary-chart_multiselect');
        //if (!selected || !Array.isArray(selected)) {
        let selected = ['hospital','sewage','dead'];
        //}

        if (selected.indexOf('sewage') === -1 && !storage.getData('sewage_set')) {
            selected.push('sewage');
            storage.updateData('sewage_set', true);
        }

        return selected;
    }

    /**
     * Initialize
     *
     * @param {Object} data - covid data
     * @param {Object} options - chart options
     */
    function init(data, options) {
        // Initially selected
        const selected = getSelected();

        const datasets = createChartData(data, window.config, selected);
        thisChart = new CovidChart('summary-chart', {
            datasets,
            chartOptions: {
                options: {
                    interaction: {
                        mode: 'index',
                        axis: 'x',
                        intersect: false
                    },
                    plugins: {
                        tooltip: {
                            bodySpacing: 5,
                        },
                    },
                    scales: {
                        x: {
                            type: 'time',
                            display: true,
                            time: {
                                unit: 'day',
                                displayFormats: {
                                    day: 'D.M.YY',
                                }
                            },
                            ticks: {
                                major: {
                                    fontStyle: 'bold',
                                    fontColor: '#FF0000'
                                }
                            }
                        },
                        y: {
                            type: 'linear',
                            position: 'left',
                            ticks: {
                                display: false
                            }
                        },
                        y2: {
                            type: 'linear',
                            position: 'right',
                            display: false,
                        },
                        y3: {
                            type: 'linear',
                            position: 'right',
                            display: false,
                            min: 0,
                            max: 50 // highestCases
                        },
                        y4: {
                            type: 'linear',
                            position: 'right',
                            display: false,
                            min: 0,
                            max: 100
                        },
                        y5: {
                            id: 'E',
                            type: 'linear',
                            position: 'right',
                            display: false,
                        },
                        y6: {
                            id: 'F',
                            type: 'linear',
                            position: 'right',
                            display: false,
                        }/*,
                        y7: {
                            type: 'linear',
                            position: 'right',
                            display: false,
                        },
                        y8: {
                            type: 'linear',
                            position: 'right',
                            display: false,
                        }*/
                    }
                }
            },
            delayAnnotation: true,
            config: window.config,
            tooltips: {
                elementId: 'summary-chart-tooltip'
            },
            multiselect: multiselectSettings
        });
    }

    function reload(data, reloadRate = false) {
        const selected = getSelected();
        const datasets = createChartData(data, window.config, selected);
        if (reloadRate) {
            thisChart.updateMultiselect(datasets, multiselectSettings);
        }
        thisChart.updateDatasets(datasets);
    }

    return {
        init,
        reload
    }

})();

export default summary;
